import { Injectable } from '@angular/core';
import { BehaviorSubject } from "rxjs";
import { CognitoAuthenticatedUser } from "../models";
import {
  fetchUserAttributes,
  signOut,
  updateUserAttributes,
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
  updatePassword,
  confirmResetPassword,
  ConfirmUserAttributeInput,
  SendUserAttributeVerificationCodeInput,
  UpdatePasswordInput,
} from "aws-amplify/auth";
import { Router } from "@angular/router";
import { DataStorageService } from "./data-storage.service";
import { AuthLocaleService } from "./auth-locale.service";

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  /** What Amplify calls a session that is simply not there. */
  private static readonly NO_SESSION_ERROR = 'UserUnAuthenticatedException';

  constructor(public router: Router,
              public dataStorageService: DataStorageService,
              private authLocale: AuthLocaleService) {
  }

  private _user$ = new BehaviorSubject<CognitoAuthenticatedUser | null>(null);

  get user$() {
    return this._user$.asObservable();
  }

  get user(){
    return this._user$.value;
  }

  setUser(user: CognitoAuthenticatedUser | null) {
    this._user$.next(user);
  }

  async logout() {
    await signOut();
    this.setUser(null);
    this.dataStorageService.logout();
    this.router.navigate(['/auth']);
  }

  async handleFetchUserAttributes() {
    try {
      const userAttributes = await fetchUserAttributes();
      if (userAttributes) {
        const user = new CognitoAuthenticatedUser(
          userAttributes.email_verified === 'true',
          userAttributes.email!,
          userAttributes.sub
        );
        this.setUser(user);
        return true;
      } else {
        this.setUser(null)
        this.sendToLogin();
        return false;
      }
    } catch (error) {
      const name = (error as any)?.name;
      console.log(name)
      this.setUser(null)
      // `signOut` ends a session. When there never was one it has nothing to
      // end - but it does clear Amplify's OAuth keys, and the link-complete
      // page writes those keys in the same moment to start its trip to Google.
      // Clearing them there makes the trip come back with a code that can no
      // longer be exchanged, and the customer lands back on the login screen.
      if (name !== AccountService.NO_SESSION_ERROR) {
        await signOut()
      }
      this.sendToLogin();
      return false;
    }
  }

  /**
   * Sends a browser without a session to the login screen.
   *
   * The pages under `/auth` are the ones that expect no session - the login
   * screen itself, and the page that finishes the contract-email flow. Moving
   * one of them to `/auth` would take the customer off the page they were sent
   * to. `window.location` is read, not `Router.url`, because this runs during
   * bootstrap, before the router has turned the URL into route segments.
   */
  private sendToLogin(): void {
    if (window.location.pathname.startsWith('/auth')) {
      return;
    }

    this.router.navigate(['/auth']);
  }

  async handleUpdateEmailAndNameAttributes(
    { email, firstName, lastName }:
    Partial<{ email: string, firstName: string, lastName: string }>) {
    let attributesToUpdate: Partial<{ email: string, given_name: string, family_name: string }> = {}

    if (email && validateEmail(email)) {
      attributesToUpdate.email = email;
    }

    if (firstName && firstName.length) {
      attributesToUpdate.given_name = firstName;
    }

    if (lastName && lastName.length) {
      attributesToUpdate.family_name = lastName;
    }

    try {
      const attributes = await updateUserAttributes({
        userAttributes: attributesToUpdate
      });
      return attributes;
    } catch (error) {
      return null;
    }
  }

  async handleConfirmUserAttribute({ userAttributeKey, confirmationCode }: ConfirmUserAttributeInput) {
    try {
      const res = await confirmUserAttribute({ userAttributeKey, confirmationCode });
      return res;
    } catch (error) {
      return 'error';
    }
  }

  async handleSendUserAttributeVerificationCode({ userAttributeKey }: SendUserAttributeVerificationCodeInput) {
    try {
      const res = await sendUserAttributeVerificationCode({ userAttributeKey });
      return res;
    } catch (error) {
      return 'error';
    }
  }

  async handleUpdatePassword({ oldPassword, newPassword }: UpdatePasswordInput) {
    try {
      const res = await updatePassword({ oldPassword, newPassword });
      return res;
    } catch (error) {
      return 'error';
    }
  }

  async sendCode(){
    try {
      const res = await this.authLocale.resetPassword({ username: this.user!.email });
      return res;
    } catch (e) {
      return 'error';
    }
  }

  async handleResetPassword({code, newPassword}: {code: string, newPassword: string}) {
    try {
      const res = await confirmResetPassword({username: this.user!.email, newPassword: newPassword, confirmationCode: code});
      return res;
    } catch (e) {
      return 'error';
    }
  }
}

function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
